'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireBrandList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-brand/search');
};

export const useTireBrandDataList = (page, rowsPerPage, searchKey, searchValue, selectedCompanyIds = '') => {
  const isFetchAll = page === undefined;

  const start = isFetchAll ? 0 : page * rowsPerPage;

  const tableName = searchKey === 'manufacture' ? 'm_manufacture' : searchKey === 'country' ? 'm_country' : null;
  const filterLike = searchKey === 'manufacture' || searchKey === 'country' ? 'name' : searchKey;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const builder = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('manufacture', ['name'])
      .addJoin('country', ['name'])
      .setOrder(['name'], true);

    const companyIdsStr = selectedCompanyIds ? String(selectedCompanyIds) : '';
    if (companyIdsStr && companyIdsStr !== '') {
      if (companyIdsStr.includes(',')) {
        builder.in('company_id', companyIdsStr);
      } else {
        builder.equal('company_id', companyIdsStr);
      }
    } else {
      builder.filterByCompany();
    }

    if (searchValue && searchValue.trim() !== '') {
      if (searchKey !== 'all') {
        builder.like(filterLike, searchValue, 'AND', tableName);
      }
    }

    const built = builder.build();

    if (searchValue && searchValue.trim() !== '' && searchKey === 'all') {
      built.filters = [
        { name: 'code_internal', value: searchValue },
        { name: 'name', value: searchValue },
        { name: 'name', table_name: 'm_manufacture', value: searchValue },
        { name: 'name', table_name: 'm_country', value: searchValue }
      ];
    }

    return built;
  }, [start, rowsPerPage, searchKey, searchValue, filterLike, tableName, isFetchAll, selectedCompanyIds]);

  const key = useMemo(() => ['/tire-brand/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getTireBrandById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/tire-brand/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tire brand by ID:', error);
    throw error;
  }
};

export const createTireBrand = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/tire-brand/new${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireBrandList();
    return response.data;
  } catch (error) {
    console.error('Error creating tire brand:', error);
    throw error;
  }
};

export const updateTireBrand = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.put(`/tire-brand/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireBrandList();
    mutate(`/tire-brand/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating tire brand:', error);
    throw error;
  }
};

export const deleteTireBrand = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/tire-brand/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireBrandList();
    return response.data;
  } catch (error) {
    console.error('Error deleting tire brand:', error);
    throw error;
  }
};
